// ย่อรูปสินค้าในเบราว์เซอร์ให้ไม่เกินเพดานของเซิร์ฟเวอร์ (D-143 ส่วนที่ 1, WO-P2-M11-025 FREEZE)

/** เพดานจริงของเซิร์ฟเวอร์ — เกินนี้เซิร์ฟเวอร์ปฏิเสธด้วย 413 PRODUCT_IMAGE_TOO_LARGE */
export const PRODUCT_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

/** เป้าหมายเผื่อไว้ใต้เพดานจริง กันการนับไบต์ของ container ไม่ตรงกันเป๊ะ */
const TARGET_BYTES = 4_500_000;

/** ชนิดที่เซิร์ฟเวอร์รับตรง ๆ — นอกชุดนี้ต้องแปลงเป็น JPEG เสมอไม่ว่าขนาดจะเล็กแค่ไหน */
const SERVER_SUPPORTED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

/** ลดคุณภาพทีละ 0.1 ลงถึง 0.6 ก่อน ค่อยลดขนาดภาพ — ลำดับนี้ตาย ตัวเข้ารหัสปลอมในเทสต์ตรวจตรง ๆ */
const QUALITY_STEPS = [0.9, 0.8, 0.7, 0.6] as const;
const SCALE_DECAY = 0.85;
const MAX_ROUNDS = 12;

export interface DecodedImage {
  readonly width: number;
  readonly height: number;
  readonly source: CanvasImageSource;
  readonly close?: () => void;
}

/** inject ได้เพื่อทดสอบลำดับคุณภาพ/สเกลโดยไม่พึ่ง canvas จริง */
export interface ResizeImageDependencies {
  readonly decode?: (file: Blob) => Promise<DecodedImage>;
  readonly encode?: (
    image: DecodedImage,
    width: number,
    height: number,
    quality: number,
  ) => Promise<Blob>;
}

export type ResizeImageResult =
  | {
      readonly status: 'ok';
      readonly blob: Blob;
      readonly filename: string;
      readonly contentType: string;
      readonly originalBytes: number;
      readonly finalBytes: number;
      readonly resized: boolean;
    }
  | { readonly status: 'decode-failed' }
  | { readonly status: 'too-large-after-resize' }
  | { readonly status: 'unsupported-type' };

function withJpegExtension(filename: string): string {
  const dot = filename.lastIndexOf('.');
  const base = dot > 0 ? filename.slice(0, dot) : filename;
  return `${base}.jpg`;
}

// "ปัดลงเล็กน้อย": ตัดทศนิยมที่เกินตำแหน่งที่สอง ไม่ใช่ round ขึ้น กันสเกลเริ่มต้นดันเกินเป้า
function roundDownSlightly(value: number): number {
  return Math.floor(value * 100) / 100;
}

async function defaultDecode(file: Blob): Promise<DecodedImage> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  return {
    width: bitmap.width,
    height: bitmap.height,
    source: bitmap,
    close: () => bitmap.close(),
  };
}

async function defaultEncode(
  image: DecodedImage,
  width: number,
  height: number,
  quality: number,
): Promise<Blob> {
  const useOffscreen = typeof OffscreenCanvas !== 'undefined';
  const canvas = useOffscreen ? new OffscreenCanvas(width, height) : document.createElement('canvas');
  if (!useOffscreen) {
    (canvas as HTMLCanvasElement).width = width;
    (canvas as HTMLCanvasElement).height = height;
  }
  const ctx = canvas.getContext('2d') as
    | CanvasRenderingContext2D
    | OffscreenCanvasRenderingContext2D
    | null;
  if (ctx === null) {
    throw new Error('ไม่สามารถสร้าง canvas context ได้');
  }
  // พื้นขาวรองใต้ภาพโปร่งใส — JPEG ไม่มี alpha channel
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(image.source, 0, 0, width, height);

  if (useOffscreen) {
    return (canvas as OffscreenCanvas).convertToBlob({ type: 'image/jpeg', quality });
  }
  return new Promise<Blob>((resolve, reject) => {
    (canvas as HTMLCanvasElement).toBlob(
      (blob) => {
        if (blob === null) {
          reject(new Error('เข้ารหัส JPEG ไม่สำเร็จ'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      quality,
    );
  });
}

/**
 * ย่อรูปให้ไม่เกิน `PRODUCT_IMAGE_MAX_BYTES` — ฟังก์ชันล้วน ไม่ throw
 *
 * (ก) ชนิดที่เซิร์ฟเวอร์รับตรง ๆ (JPEG/PNG/WebP) และขนาด ≤ เพดาน → คืนไฟล์เดิม
 * (ข) อย่างอื่น → ถอดรหัส วาดลง canvas แล้วเข้ารหัสใหม่เป็น JPEG วนลดจนผ่านเป้า
 */
export async function resizeImageToLimit(
  file: Blob,
  filename: string,
  dependencies: ResizeImageDependencies = {},
): Promise<ResizeImageResult> {
  const originalBytes = file.size;
  const contentType = file.type;

  if (SERVER_SUPPORTED_TYPES.has(contentType) && originalBytes <= PRODUCT_IMAGE_MAX_BYTES) {
    return {
      status: 'ok',
      blob: file,
      filename,
      contentType,
      originalBytes,
      finalBytes: originalBytes,
      resized: false,
    };
  }

  // ชนิดที่ไม่ใช่รูปเลย (เช่น .pdf) ไม่มีทางถอดรหัสเป็นภาพได้ — ไม่ต้องลองถอดรหัสก่อนเสีย
  if (!contentType.startsWith('image/')) {
    return { status: 'unsupported-type' };
  }

  const decode = dependencies.decode ?? defaultDecode;
  const encode = dependencies.encode ?? defaultEncode;

  let decoded: DecodedImage;
  try {
    decoded = await decode(file);
  } catch {
    return { status: 'decode-failed' };
  }

  try {
    // ห้ามขยายภาพ — สเกลเริ่มต้นไม่เกิน 1
    let scale = Math.min(1, roundDownSlightly(Math.sqrt(TARGET_BYTES / originalBytes)));

    for (let round = 0; round < MAX_ROUNDS; round += 1) {
      const quality = QUALITY_STEPS[Math.min(round, QUALITY_STEPS.length - 1)];
      // คุณภาพแตะพื้น 0.6 แล้ว (รอบที่ 5 เป็นต้นไป) จึงเริ่มลดขนาดภาพแทน
      if (round >= QUALITY_STEPS.length) {
        scale *= SCALE_DECAY;
      }
      const width = Math.max(1, Math.round(decoded.width * scale));
      const height = Math.max(1, Math.round(decoded.height * scale));

      const encoded = await encode(decoded, width, height, quality);
      if (encoded.size <= TARGET_BYTES) {
        return {
          status: 'ok',
          blob: encoded,
          filename: withJpegExtension(filename),
          contentType: 'image/jpeg',
          originalBytes,
          finalBytes: encoded.size,
          resized: true,
        };
      }
    }

    return { status: 'too-large-after-resize' };
  } finally {
    decoded.close?.();
  }
}
